import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from './services/cloudflare-api';

const AuthContext = createContext();

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Load token from localStorage on mount
  useEffect(() => {
    const loadSession = async () => {
      const savedToken = localStorage.getItem('auth_token');
      if (savedToken) {
        try {
          const response = await api.verifySession(savedToken);
          if (response.valid) {
            setToken(savedToken);
            setUser(response.user);
          } else {
            // Invalid token, clear it
            localStorage.removeItem('auth_token');
          }
        } catch (error) {
          console.error('Session verification failed:', error);
          localStorage.removeItem('auth_token');
        }
      }
      setLoading(false);
    };

    loadSession();
  }, []);

  const login = async (email, password, rememberMe = false) => {
    try {
      const response = await api.login(email, password, rememberMe);
      setToken(response.token);
      setUser(response.user);
      localStorage.setItem('auth_token', response.token);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const logout = async () => {
    try {
      if (token) {
        await api.logout(token);
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setToken(null);
      setUser(null);
      localStorage.removeItem('auth_token');
      navigate('/login');
    }
  };

  const hasPermission = (requiredRole) => {
    if (!user) return false;

    const roleHierarchy = {
      admin: 2,
      editor: 1
    };

    return (roleHierarchy[user.role] || 0) >= (roleHierarchy[requiredRole] || 0);
  };

  const canEdit = () => {
    return user && (user.role === 'admin' || user.role === 'editor');
  };

  const isAdmin = () => {
    return user && user.role === 'admin';
  };

  const isEditor = () => {
    return user && user.role === 'editor';
  };

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!user,
    login,
    logout,
    hasPermission,
    canEdit,
    isAdmin,
    isEditor
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
